import { IOSOptionalDetails } from '../../ios-web/components';
import { useEffect, useRef, useState } from 'react';
import { Button, Cluster, DataTable, Panel, StatusBadge } from '../../components';
import { ApiError, type AmoyeApi } from '../../lib/api';
import type { LocalPersonalFilingResponse, LocalPersonalPreparation, PersonalFilingProfile,
  PersonalFilingProfileResponse, PersonalFilingAuthority, PersonalPortalReportRequest } from '../../lib/personal-filing-types';
import { formatKobo } from '../return-centre';
import type { PersonalCalculationView } from '../track-b2/types';
import './styles.css';
import { PortalPreparationEditor, PortalPreparationGuide } from './portal-preparation';

const personalTaxTraceLabels: Readonly<Record<string, string>> = {
  'PIT-CHARGEABLE': 'Chargeable income after eligible deductions and rent relief',
  'PIT-BAND-1': 'First ₦800,000 of chargeable income at 0%',
  'PIT-BAND-2': 'Next ₦2,200,000 of chargeable income at 15%',
  'PIT-BAND-3': 'Next ₦9,000,000 of chargeable income at 18%',
  'PIT-BAND-4': 'Next ₦13,000,000 of chargeable income at 21%',
  'PIT-BAND-5': 'Next ₦25,000,000 of chargeable income at 23%',
  'PIT-BAND-6': 'Chargeable income above ₦50,000,000 at 25%',
  'PIT-NET': 'Total tax after verified tax credits (not below zero)',
};

export function personalTaxTraceLabel(line: { id: string; rule: string }): string {
  return personalTaxTraceLabels[line.id] ?? line.rule;
}

function PersonalTaxCalculationSources() {
  return <p className="am-personal-calculation-details__sources">The progressive bands are set out in the{' '}
    <a href="https://nass.gov.ng/documents/download/11249" rel="noreferrer" target="_blank">
      Nigeria Tax Act, 2025 — Fourth Schedule</a>. The chargeable-income worksheet and band-by-band method are also shown in the{' '}
    <a href="https://www.jrb.gov.ng/assets/2026-pit-guidelines-TJG3n9-T.pdf" rel="noreferrer" target="_blank">
      Joint Revenue Board Personal Income Tax Guidelines, 2026 — Parts F and G</a>.
  </p>;
}

export function usePersonalFilingWorkspace({ api, contextId, taxpayerName, taxYear, calculationId, calculation }: {
  api: AmoyeApi; contextId?: string; taxpayerName: string; taxYear: number; calculationId?: string; calculation?: PersonalCalculationView;
}) {
  const [savedProfile, setProfile] = useState<PersonalFilingProfileResponse>();
  const [savedFiling, setFiling] = useState<LocalPersonalFilingResponse>();
  const [savedContext, setSavedContext] = useState<string>();
  const profile = savedContext === contextId ? savedProfile : undefined;
  const filing = savedContext === contextId ? savedFiling : undefined;
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const requestSequence = useRef(0);
  const mutationInFlight = useRef(false);
  const observedCalculation = useRef(calculationId);

  useEffect(() => {
    if (!contextId || typeof api.loadPersonalFilingProfile !== 'function'
      || typeof api.loadPersonalFiling !== 'function') { setLoading(false); return; }
    const sequence = ++requestSequence.current;
    const controller = new AbortController();
    if (savedContext !== contextId) { setProfile(undefined); setFiling(undefined); setSavedContext(contextId); }
    setLoading(true); setError(''); setBusy(false);
    void Promise.allSettled([api.loadPersonalFilingProfile(contextId, controller.signal),
      api.loadPersonalFiling(contextId, controller.signal)]).then(([profileResult, filingResult]) => {
      if (sequence !== requestSequence.current || controller.signal.aborted) return;
      if (profileResult.status === 'fulfilled') setProfile(profileResult.value);
      if (filingResult.status === 'fulfilled') setFiling(filingResult.value);
      const failures = [profileResult, filingResult].filter((result) => result.status === 'rejected');
      if (failures.length) setError('Your filing details could not be fully loaded. Retry before continuing.');
      setLoading(false);
    });
    return () => { controller.abort(); ++requestSequence.current; };
  }, [api, contextId, refresh]);

  useEffect(() => {
    if (!contextId || busy || loading || observedCalculation.current === calculationId
      || typeof api.loadPersonalFiling !== 'function') return;
    const controller = new AbortController();
    const sequence = requestSequence.current;
    // A new calculation invalidates the currently displayed preparation until
    // the server binds it again. Do not reload/re-key a dirty profile editor.
    setFiling((value) => value ? { ...value, currentInputsMatch: false, canSubmit: false } : value);
    void api.loadPersonalFiling(contextId, controller.signal).then((value) => {
      if (!controller.signal.aborted && sequence === requestSequence.current) {
        observedCalculation.current = calculationId; setFiling(value);
      }
    }).catch(() => {
      if (!controller.signal.aborted && sequence === requestSequence.current) setError('Your saved calculation changed. Reload the filing details before continuing.');
    });
    return () => controller.abort();
  }, [api, contextId, calculationId, busy, loading]);

  const act = async (operation: () => Promise<void>, allowInPlaceRetry = false) => {
    if (busy || loading || !contextId || mutationInFlight.current) throw new Error('Wait for the current filing action to finish, then try again.');
    mutationInFlight.current = true;
    const sequence = ++requestSequence.current;
    setBusy(true); setError('');
    try { await operation(); }
    catch (cause) {
      if (sequence !== requestSequence.current) return;
      if (!allowInPlaceRetry || cause instanceof ApiError && cause.status === 409) {
        setError(cause instanceof ApiError && cause.status === 409
          ? 'These records changed while you were working. Reload the saved details and review them before continuing.'
          : cause instanceof Error ? cause.message : 'Your change could not be saved. Try again.');
      }
      throw cause;
    } finally { mutationInFlight.current = false; if (sequence === requestSequence.current) setBusy(false); }
  };
  const saveProfile = async (nextProfile: PersonalFilingProfile, confirmResidencyReview = false) => {
    const sequence = requestSequence.current + 1;
    await act(async () => {
      const saved = await api.savePersonalFilingProfile(contextId!, { expectedVersion: profile?.version ?? 0,
        profile: nextProfile, confirmResidencyReview });
      if (sequence !== requestSequence.current) return;
      setProfile(saved);
      // An existing signature belongs to its original profile snapshot until the server confirms freshness.
      setFiling((value) => value ? { ...value, currentInputsMatch: false } : value);
      const refreshed = await api.loadPersonalFiling(contextId!);
      if (sequence === requestSequence.current) setFiling(refreshed);
    });
  };
  const prepare = async () => {
    if (!calculationId) return;
    const sequence = requestSequence.current + 1;
    await act(async () => {
      const result = await api.preparePersonalFiling(contextId!, calculationId);
      if (sequence === requestSequence.current) setFiling(result);
    });
  };
  const sign = async () => {
    const preparation = filing?.preparation;
    if (!preparation || preparation.status !== 'PREPARED' || !filing.currentInputsMatch) return;
    const sequence = requestSequence.current + 1;
    await act(async () => {
      const result = await api.signPersonalFiling(contextId!, { preparationId: preparation.id,
        expectedVersion: preparation.version, artifactSha256: preparation.artifactSha256,
        declarationSha256: preparation.artifact.declaration.sha256, confirmed: true });
      if (sequence === requestSequence.current) setFiling(result);
    });
  };
  const reportPortal = async (body: PersonalPortalReportRequest) => {
    if (!filing?.preparation || body.preparationId !== filing.preparation.id
      || body.artifactSha256 !== filing.preparation.artifactSha256) throw new Error('Reload the signed return before recording this submission.');
    const sequence = requestSequence.current + 1;
    await act(async () => {
      const result = await api.reportPersonalPortalSubmission(contextId!, body);
      if (sequence === requestSequence.current) setFiling(result);
    }, true);
  };
  const localEnabled = filing?.policy.mode === 'LOCAL_EXPERIENCE';
  const feedback = error && <div role="alert"><p>{error}</p><Button disabled={busy || loading}
    onClick={() => {
      if (document.querySelector('[data-amoye-unsaved="true"]')
        && !window.confirm('Reloading will replace unsaved filing details with the saved version. Continue?')) return;
      setRefresh((value) => value + 1);
    }} size="small" tone="secondary">Reload filing details</Button></div>;
  const panel = (view: 'return' | 'filing') => <>
    {feedback}
    {loading && <p role="status">Loading your saved filing details…</p>}
    {localEnabled && <PersonalFilingPanel view={view} calculation={calculation} busy={busy || loading || Boolean(error)} filing={filing}
      authority={profile?.authorities.find((item) => item.code === filing.preparation?.artifact.authorityCode)}
      onReportPortal={reportPortal}
      onExport={async (preparation) => {
        const result = await api.exportPersonalFiling(contextId!, preparation.id, preparation.artifactSha256);
        if (result.mimeType !== 'application/pdf' || result.artifactSha256 !== preparation.artifactSha256) {
          throw new Error('The exported file does not match this return. Reload and try again.');
        }
        const bytes = Uint8Array.from(atob(result.contentBase64), (character) => character.charCodeAt(0));
        const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)),
          (byte) => byte.toString(16).padStart(2, '0')).join('');
        if (digest !== result.sha256) throw new Error('The PDF did not pass its integrity check. Please try again.');
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        const link = document.createElement('a'); link.href = url; link.download = `Amoye-return-${preparation.artifact.taxYear}.pdf`;
        document.body.append(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }}
      onPrepare={prepare} onSign={sign} hasCalculation={Boolean(calculationId)} />}
  </>;
  return {
    localEnabled,
    filing,
    authorityLabel: profile?.authorities.find((authority) => authority.code === profile.profile?.authoritySelections
      .find((selection) => selection.taxYear === taxYear)?.authorityCode)?.name,
    profilePanel: <>{feedback}{profile && <FilingProfileForm key={`${contextId}:${profile.version}:${refresh}`}
      response={profile} taxpayerName={taxpayerName} taxYear={taxYear} busy={busy || loading || Boolean(error)}
      onReload={() => {
        if (document.querySelector('[data-amoye-unsaved="true"]')
          && !window.confirm('Reloading will replace unsaved filing details with the saved version. Continue?')) return;
        setRefresh((value) => value + 1);
      }}
      onSave={saveProfile} />}</>,
    returnPanel: panel('return'),
    filingPanel: panel('filing'),
    prepare,
  };
}

export function FilingProfileForm({ response, taxpayerName, taxYear, busy, onSave, onReload }: {
  response: PersonalFilingProfileResponse; taxpayerName: string; taxYear: number; busy: boolean;
  onSave(profile: PersonalFilingProfile, confirmResidencyReview?: boolean): Promise<void>;
  onReload?(): void;
}) {
  const initial = response.profile ?? { taxpayerName, residences: [], authoritySelections: [] };
  const [draft, setDraft] = useState<PersonalFilingProfile>(() => structuredClone(initial));
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  useEffect(() => { setReviewConfirmed(false); }, [draft]);
  const selectedAuthority = draft.authoritySelections.find((selection) => selection.taxYear === taxYear)?.authorityCode ?? '';
  const authority = response.authorities.find((item) => item.code === selectedAuthority);
  const portalUrl = safeAuthorityUrl(authority?.portal?.url);
  const websiteUrl = safeAuthorityUrl(authority?.websiteUrl);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial) || reviewConfirmed;
  const review = draft.authoritySelections.find((s) => s.taxYear === taxYear)?.residencyReview
    ?? { basis: '', discrepancyNotes: '', ruleBasis: '', evidenceIds: [] };
  const updateReview = (change: Partial<typeof review>) => setDraft((value) => ({ ...value,
    authoritySelections: value.authoritySelections.map((s) => s.taxYear !== taxYear ? s : {
      ...s, residencyReview: { ...review, ...change } }) }));
  const disabled = busy || !response.canEdit;
  return <Panel title="Taxpayer and filing authority" description={`Confirm your name and choose the filing authority for ${taxYear}. Residence history and documents are optional here; complete or remove any residence row you add. These are your declarations, not government verification.`}>
    <form className="am-personal-filing-form" data-amoye-unsaved={dirty ? 'true' : 'false'} onSubmit={(event) => {
      event.preventDefault(); if (!dirty || disabled) return;
      void (reviewConfirmed ? onSave(draft, true) : onSave(draft)).catch(() => undefined);
    }}>
      <fieldset disabled={disabled}>
        <div className="am-personal-filing-fields"><label><span>Taxpayer name</span><input required minLength={2} maxLength={200}
          value={draft.taxpayerName} onChange={(event) => setDraft((value) => ({ ...value, taxpayerName: event.target.value }))} /></label>
        <label><span>Filing authority · {taxYear}</span><select required value={selectedAuthority} onChange={(event) => {
          const authorityCode = event.target.value;
          setDraft((value) => {
            // A payer ID belongs to its authority, not to every state the user selects.
            const { portalPreparation: _oldWorksheet, ...previous } = value.authoritySelections.find((s) => s.taxYear === taxYear) ?? {};
            return { ...value, authoritySelections: [...value.authoritySelections.filter((selection) => selection.taxYear !== taxYear),
              ...(authorityCode ? [{ ...previous, taxYear, taxType: 'PIT' as const, authorityCode }] : [])] };
          });
        }}><option value="">Choose the applicable authority</option>{response.authorities.map((authority) =>
          <option key={authority.code} value={authority.code}>{authority.name}</option>)}</select></label></div>
        <IOSOptionalDetails title="Authority guidance and portal details">
        {authority && <aside className="am-personal-authority-guidance" aria-label="Selected authority guidance">
          <h3>{authority.name}</h3>
          {authority.characterization?.filingGuidance && <p>{authority.characterization.filingGuidance}</p>}
          {authority.portal && portalUrl && <p><a href={portalUrl} target="_blank" rel="noopener noreferrer">
            {authority.portal.name} · official portal</a></p>}
          {!portalUrl && websiteUrl && <p><a href={websiteUrl} target="_blank" rel="noopener noreferrer">
            Authority website</a></p>}
          {authority.portal?.observedServices.length ? <p>Portal services: {authority.portal.observedServices.join(', ')}.</p> : null}
          <small>Amoye will not send your saved details to this website when you open the link.</small>
        </aside>}
        {selectedAuthority && <PortalPreparationEditor key={selectedAuthority} taxYear={taxYear}
          value={draft.authoritySelections.find((s) => s.taxYear === taxYear)?.portalPreparation}
          onChange={(portalPreparation) => setDraft((value) => ({ ...value, authoritySelections: value.authoritySelections
            .map((s) => s.taxYear === taxYear ? { ...s, portalPreparation } : s) }))} />}
        </IOSOptionalDetails>
        <IOSOptionalDetails title="Residence history (optional)" open={draft.residences.length > 0}>
        <p>Residence changes are recorded below. They do not automatically split your tax or change a previously prepared return.</p>
        {draft.residences.map((residence, index) => <div className="am-personal-residence-row" key={index}>
          <label><span>Address · residence {index + 1}</span><input maxLength={500} value={residence.address ?? ''}
            onChange={(event) => setDraft((value) => ({ ...value, residences: value.residences.map((r, at) =>
              at === index ? { ...r, address: event.target.value } : r) }))} /></label>
          <label><span>State or FCT · residence {index + 1}</span><select required value={residence.stateCode} onChange={(event) => {
            const stateCode = event.target.value; setDraft((value) => ({ ...value, residences: value.residences.map((item, at) => at === index ? { ...item, stateCode } : item) }));
          }}><option value="">Select a state</option>{response.residenceStates.map((state) => <option key={state.code} value={state.code}>{state.name}</option>)}</select></label>
          <label><span>Resident from</span><input required type="date" value={residence.effectiveFrom} onChange={(event) => {
            const effectiveFrom = event.target.value; setDraft((value) => ({ ...value, residences: value.residences.map((item, at) => at === index ? { ...item, effectiveFrom } : item) }));
          }} /></label>
          <label><span>Last day resident (leave blank if current)</span><input type="date" min={residence.effectiveFrom || undefined}
            value={residence.effectiveTo ?? ''} onChange={(event) => {
              const effectiveTo = event.target.value; setDraft((value) => ({ ...value, residences: value.residences.map((item, at) => {
                if (at !== index) return item; const { effectiveTo: _previousEnd, ...rest } = item;
                return { ...rest, ...(effectiveTo ? { effectiveTo } : {}) };
              }) }));
            }} /></label>
          <Button aria-label={`Remove residence ${index + 1}`} onClick={() => setDraft((value) => ({ ...value,
            residences: value.residences.filter((_, at) => at !== index) }))} size="small" tone="secondary" type="button">Remove</Button>
          <label><input type="checkbox" checked={residence.additionalHome ?? false} onChange={(event) => setDraft((value) => ({ ...value,
            residences: value.residences.map((r, at) => at === index ? { ...r, additionalHome: event.target.checked } : r) }))} /> Another available home</label>
          <label><span>Relevant circumstances · residence {index + 1}</span><textarea maxLength={2000}
            placeholder="For example, when this home was available or relevant work arrangements. Do not enter NIN or banking details."
            value={residence.circumstances ?? ''} onChange={(event) => setDraft((value) => ({ ...value,
              residences: value.residences.map((r, at) => at === index ? { ...r, circumstances: event.target.value } : r) }))} /></label>
        </div>)}
        <Button disabled={draft.residences.length >= 100} onClick={() => setDraft((value) => ({ ...value,
          residences: [...value.residences, { stateCode: '', effectiveFrom: '' }] }))} size="small" tone="secondary" type="button">Add residence</Button>
        </IOSOptionalDetails>
        {selectedAuthority && <IOSOptionalDetails title="Why this authority applies (optional)" open={Boolean(review.basis || review.discrepancyNotes || review.ruleBasis || review.evidenceIds.length)}><fieldset><legend>Why this filing authority applies (optional)</legend>
          <p>Keep a short explanation of your choice, especially when home or work details differ. This is your record, not a required authority questionnaire. You can save and calculate without completing it or uploading documents.</p>
          {response.residencyReview?.prompts.map((prompt) => <p key={prompt} role="note">{prompt}</p>)}
          <label><span>Basis for choosing this authority</span><textarea maxLength={4000} value={review.basis}
            onChange={(e) => updateReview({ basis: e.target.value })} /></label>
          <label><span>Discrepancies, explanation and outstanding follow-up</span><textarea maxLength={4000} value={review.discrepancyNotes}
            onChange={(e) => updateReview({ discrepancyNotes: e.target.value })} /></label>
          <label><span>Rule or authority guidance relied on (optional)</span><textarea maxLength={2000} value={review.ruleBasis}
            onChange={(e) => updateReview({ ruleBasis: e.target.value })} /></label>
          <p>Supporting documents for this taxpayer and year. Add and review documents in Evidence first; no document is mandatory just to calculate.</p>
          {(response.availableEvidence ?? []).map((item) => <label key={item.id}><input type="checkbox"
            checked={review.evidenceIds.includes(item.id)} onChange={(e) => updateReview({ evidenceIds: e.target.checked
              ? [...review.evidenceIds, item.id] : review.evidenceIds.filter((id) => id !== item.id) })} /> {item.filename}</label>)}
          {review.evidenceIds.filter((id) => !response.availableEvidence?.some((e) => e.id === id)).map((id) => <p key={id}>
            Linked document unavailable. <Button type="button" tone="secondary" onClick={() => updateReview({ evidenceIds:
              review.evidenceIds.filter((value) => value !== id) })}>Remove unavailable link</Button></p>)}
          <label><input type="checkbox" checked={reviewConfirmed} disabled={!review.basis.trim()}
            onChange={(e) => setReviewConfirmed(e.target.checked)} /> I reviewed the current facts, explanation and supporting documents.</label>
          <small>This records your review, not a government or identity-provider verification. Unresolved points remain visible.</small>
        </fieldset></IOSOptionalDetails>}
      </fieldset>
      <Cluster><Button disabled={disabled || !dirty} type="submit">Save</Button>
        {onReload && <Button disabled={busy} type="button" tone="secondary" onClick={onReload}>Reload saved details and evidence</Button>}
        {response.savedAt && <small>Saved {new Date(response.savedAt).toLocaleString()}</small>}</Cluster>
      {!response.canEdit && <p>These details are read-only in your current context.</p>}
    </form>
  </Panel>;
}

export function PersonalFilingPanel({ filing, busy, hasCalculation, onPrepare, onSign,
  authority, onReportPortal, onExport, view = 'filing', calculation }: {
  view?: 'return' | 'filing';
  calculation?: PersonalCalculationView;
  filing: LocalPersonalFilingResponse; busy: boolean; hasCalculation: boolean;
  authority?: PersonalFilingAuthority;
  onPrepare(): Promise<void>; onSign(): Promise<void>;
  onReportPortal?(body: PersonalPortalReportRequest): Promise<void>;
  onExport?(preparation: LocalPersonalPreparation): Promise<void>;
}) {
  const preparation = filing.preparation;
  const artifact = preparation?.artifact;
  const [confirmed, setConfirmed] = useState(false);
  const [printError, setPrintError] = useState('');
  const [exporting, setExporting] = useState(false);
  useEffect(() => { setConfirmed(false); setPrintError(''); }, [preparation?.id, preparation?.artifactSha256, filing.currentInputsMatch]);
  const needsPreparation = !preparation || !filing.currentInputsMatch;
  return <Panel id={view === 'return' ? 'filing-review' : undefined} tabIndex={view === 'return' ? -1 : undefined}
    title={view === 'return' ? 'Review and sign your return' : 'File your return'}
    description={view === 'return' ? 'Review the calculation, filing authority and declaration.' : 'Check your signed return, use the applicable authority’s filing service and record its reference.'}>
    {filing.nextAction && <p>{filing.nextAction}</p>}
    {view === 'return' && !preparation && calculation && <details aria-label="How your estimate was calculated"
      className="am-personal-calculation-details" id="calculation-trace" tabIndex={-1}>
      <summary><h3>How your estimate was calculated</h3></summary><div className="am-personal-calculation-details__body">
      <p>Amoye first determines chargeable income, applies each statutory rate only to the part of income within that band, totals the band amounts, and then deducts verified tax credits.</p>
      <DataTable caption="Calculation details" columns={[
        { id: 'rule', header: 'Calculation step', render: (line) => personalTaxTraceLabel(line) },
        { id: 'result', header: 'Result', render: (line) => line.resultLabel },
      ]} rows={calculation.lines} getRowKey={(line) => line.id} />
      <PersonalTaxCalculationSources />
      <ul>{calculation.citations.map((citation) => <li key={citation.id}><a href={citation.url} rel="noreferrer" target="_blank">{citation.title}</a> · {citation.locator}</li>)}</ul>
      </div></details>}
    {needsPreparation && <><p>{preparation ? 'Your saved details or calculation have changed. Prepare a new version before signing; the earlier record is preserved.'
      : 'Save your filing details and calculate your tax position, then prepare the return.'}</p>
      {view === 'return' ? <Button disabled={busy || !hasCalculation || !filing.canPrepare} onClick={() => void onPrepare().catch(() => undefined)}>
        {preparation ? 'Prepare updated return' : 'Prepare return'}</Button> : <p>Open Tax return to prepare and sign the version you want to submit.</p>}</>}
    {preparation && artifact && <div className="am-personal-filing-preview">
      <Cluster><h3>{artifact.returnType}</h3><StatusBadge tone={preparation.status === 'SIGNED' ? 'success' : 'info'}>
        {preparation.status === 'SIGNED' ? 'Signed' : 'Prepared'}</StatusBadge>
        {!filing.currentInputsMatch && <StatusBadge tone="warning">Update needed</StatusBadge>}</Cluster>
      {!filing.currentInputsMatch && <p role="status">This preserved return is based on earlier saved details or an earlier calculation. Prepare an updated version before signing or filing again.</p>}
      <dl className="am-personal-filing-summary"><div><dt>Taxpayer</dt><dd>{artifact.taxpayerName}</dd></div>
        <div><dt>Tax year</dt><dd>{artifact.taxYear}</dd></div><div><dt>Filing authority</dt><dd>{artifact.authorityLabel}</dd></div>
        <div><dt>Income</dt><dd>{formatKobo(artifact.facts.grossIncomeKobo)}</dd></div>
        <div><dt>Tax credits</dt><dd>{formatKobo(artifact.facts.taxCreditsKobo)}</dd></div>
        <div><dt>Estimated balance</dt><dd>{formatKobo(artifact.liabilityKobo)}</dd></div></dl>
      {view === 'return' && <><details aria-label="How your estimate was calculated"
        className="am-personal-calculation-details" id="calculation-trace" tabIndex={-1}>
        <summary><h3>How your estimate was calculated</h3></summary><div className="am-personal-calculation-details__body">
        <p>Amoye first determines chargeable income, applies each statutory rate only to the part of income within that band, totals the band amounts, and then deducts verified tax credits.</p>
        <DataTable caption="Prepared return calculation" columns={[
        { id: 'rule', header: 'Calculation step', render: (line) => <>{personalTaxTraceLabel(line)}
          {line.rateBasisPoints !== undefined && <small className="am-feature-muted">Rate: {line.rateBasisPoints / 100}%</small>}
          <small className="am-feature-muted">Income or amounts used: {line.operandsKobo.map(formatKobo).join(' · ')}</small></> },
        { id: 'amount', header: 'Tax or result from this step', render: (line) => formatKobo(line.resultKobo) },
      ]} rows={artifact.calculation.trace} getRowKey={(line) => line.id} />
        <PersonalTaxCalculationSources />
        <ul>{artifact.calculation.sourceCitations.map((citation) => <li key={citation.id}>
          <a href={citation.url} rel="noreferrer" target="_blank">{citation.title}</a> · {citation.relevantLocator}
        </li>)}</ul>
        </div></details>
      <section aria-label="Basis for the filing authority"><h3>Basis for the filing authority</h3>
      <p>{artifact.authorityLabel} · Tax year {artifact.taxYear}</p>
      {artifact.residencyReview ? <>
        <p>{artifact.residencyReview.basis || 'No explanation recorded.'}</p>
        <p>{artifact.residencyReview.discrepancyNotes}</p><p>{artifact.residencyReview.ruleBasis}</p>
        <p>{artifact.residencyReview.reviewState === 'EXPLANATION_RECORDED' ? 'Your explanation is recorded.' : 'Follow-up is needed.'} Residence is not independently verified.</p>
        {artifact.residencyReview.prompts.map((prompt) => <p key={prompt}>{prompt}</p>)}
        <ul>{artifact.residencyReview.evidence.map((item) => <li key={item.id}>{item.filename}</li>)}</ul>
      </> : <p>This is the authority selected in your saved filing details. You can add your residence facts and explanation in Tax position.</p>}
      </section>
      <h3>Your declaration</h3><p>{artifact.declaration.text}</p>
      {preparation.signature ? <p role="status">Declaration recorded {new Date(preparation.signature.signedAt).toLocaleString()}.
        {' '}Your signature confirms this version. Submission is a separate step in Filing.</p>
        : filing.currentInputsMatch && <><label className="am-personal-declaration"><input type="checkbox" checked={confirmed}
          disabled={busy} onChange={(event) => setConfirmed(event.target.checked)} />
          <span>I have reviewed this version and confirm the declaration above.</span></label>
        <Button disabled={busy || !confirmed} onClick={() => void onSign().catch(() => undefined)}>Sign this return</Button></>}</>}
      <Cluster>{onExport && <Button disabled={exporting} onClick={() => {
        setExporting(true); setPrintError('');
        void onExport(preparation).catch((error: unknown) => setPrintError(error instanceof Error ? error.message : 'PDF export failed. Try again.'))
          .finally(() => setExporting(false));
      }}>{exporting ? 'Preparing PDF…' : 'Export return PDF'}</Button>}<Button onClick={() => {
        setPrintError(''); try { printPersonalFilingPreview(preparation); }
        catch { setPrintError('Allow pop-ups for Amoye, then try printing again.'); }
      }} tone="secondary">Print / Save PDF</Button></Cluster>
      <small>This is your Amoye return summary, not a prescribed authority filing form.</small>
      {printError && <p role="alert">{printError}</p>}
    </div>}
    {view === 'filing' && <>{preparation && <section className="am-personal-filing-route" aria-label="How to file this return">
      <h3>File through the tax authority</h3>
      <p>Use the applicable authority’s filing service, then save the receipt or submission reference here so your Amoye record stays current.</p>
    </section>}
    {preparation && <PortalPreparationGuide key={preparation.id} preparation={preparation} />}
    {preparation && authority?.code === artifact?.authorityCode && authority &&
      <PortalHandoff authority={authority} />}
    {preparation?.status === 'SIGNED' && onReportPortal && filing.canReportPortal &&
      <PortalReportForm key={`${preparation.id}:${preparation.artifactSha256}`} preparation={preparation}
        busy={busy} onReport={onReportPortal} />}
    {preparation && !preparation.signature && <p>Sign this prepared version before recording a submission. Documents are not required to save the record.</p>}
    {Boolean(filing.portalReports?.length) && <details aria-label="Authority submission records">
      <summary>Recorded authority submissions ({filing.portalReports?.length})</summary>
      <p>These are your reported submissions, based on the receipt or reference you entered. Amoye does not independently validate them with the authority.</p>
      <ol>{filing.portalReports?.map((report) => <li key={report.id}>
        <strong>{report.authorityLabel}</strong> · User-reported
        <p>Submitted {new Date(report.submittedAt).toLocaleString()} · Reference: {report.reference}</p>
        <small>Recorded in Amoye {new Date(report.reportedAt).toLocaleString()}.</small>
      </li>)}</ol>
    </details>}
    </>}
  </Panel>;
}

export function PortalHandoff({ authority }: { authority: PersonalFilingAuthority }) {
  const handoff = authority.portalHandoff;
  if (!handoff) return null;
  return <section aria-label="Authority portal instructions">
    <h3>{handoff.title}</h3><p>{handoff.templateNote}</p>
    <p>Check the taxpayer, tax year and signed return before continuing to the authority’s portal.</p>
    <ol>{handoff.steps.map((step) => <li key={step.id}><strong>{step.title}</strong>
      <p>{step.detail}</p>{safeAuthorityUrl(step.url) && <a href={safeAuthorityUrl(step.url)}
        target="_blank" rel="noopener noreferrer">Open official guidance or portal</a>}</li>)}</ol>
    <small>Opening a link does not send your return or sign you into the authority portal.</small>
  </section>;
}

export function PortalReportForm({ preparation, busy, onReport, onEditingChange }: {
  preparation: LocalPersonalPreparation; busy: boolean;
  onReport(body: PersonalPortalReportRequest): Promise<void>;
  onEditingChange?(editing: boolean): void;
}) {
  const [reference, setReference] = useState('');
  const [submittedAt, setSubmittedAt] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const attempt = useRef<{ fingerprint: string; idempotencyKey: string } | undefined>(undefined);
  const saving = useRef(false);
  const disabled = busy || pending;
  const editing = Boolean(reference || submittedAt || confirmed);
  useEffect(() => {
    onEditingChange?.(editing);
    return () => onEditingChange?.(false);
  }, [editing, onEditingChange]);
  return <form className="am-personal-filing-form" aria-label="Record an authority submission"
    data-amoye-unsaved={reference || submittedAt ? 'true' : 'false'} onSubmit={(event) => {
      event.preventDefault();
      const instant = new Date(submittedAt);
      if (disabled || saving.current || !confirmed || !reference.trim() || !Number.isFinite(instant.getTime())) return;
      if (instant.getTime() > Date.now()) { setMessage('Choose the date and time you already submitted the return.'); return; }
      if (preparation.signature && instant.getTime() < Date.parse(preparation.signature.signedAt)) {
        setMessage('The submission date must be on or after you signed this version.'); return;
      }
      const body = { preparationId: preparation.id, artifactSha256: preparation.artifactSha256,
        submittedAt: instant.toISOString(), reference: reference.trim() };
      const fingerprint = JSON.stringify(body);
      if (attempt.current?.fingerprint !== fingerprint) attempt.current = { fingerprint, idempotencyKey: crypto.randomUUID() };
      saving.current = true; setPending(true); setMessage('');
      void onReport({ ...body, idempotencyKey: attempt.current.idempotencyKey }).then(() => {
        setReference(''); setSubmittedAt(''); setConfirmed(false); attempt.current = undefined;
        setMessage('Your authority submission record was saved. Amoye has not validated it with the authority.');
      }).catch(() => setMessage('The record could not be confirmed as saved. Retry with the same details.'))
        .finally(() => { saving.current = false; setPending(false); });
    }}>
    <h3>Record a submission you completed</h3>
    <p>To save: enter the required authority receipt or submission reference, choose a submission time on or after signing this version, and confirm you kept the authority’s confirmation. Amoye records the value you enter but does not validate it with the authority.</p>
    <p>This records your report for this signed version and {preparation.artifact.authorityLabel}.
      It does not submit a return or verify acceptance.</p>
    {preparation.signature && <small>This version was signed {new Date(preparation.signature.signedAt).toLocaleString()}.
      {' '}Enter a submission time on or after that confirmation.</small>}
    <fieldset disabled={disabled}><div className="am-personal-filing-fields">
      <label><span>Authority submission receipt or reference</span><input required maxLength={160} value={reference}
        onChange={(event) => { setReference(event.target.value); setConfirmed(false); }} /></label>
      <label><span>When you submitted (your local time)</span><input required type="datetime-local" step="1" value={submittedAt}
        onChange={(event) => { setSubmittedAt(event.target.value); setConfirmed(false); }} /></label>
    </div><label className="am-personal-declaration"><input type="checkbox" checked={confirmed}
      onChange={(event) => setConfirmed(event.target.checked)} />
      <span>I submitted this version through the authority's portal or confirmed filing channel and have kept the confirmation.</span></label></fieldset>
    <Button type="submit" disabled={disabled || !confirmed || !reference.trim() || !submittedAt}>Save submission record</Button>
    {message && <p role="status">{message}</p>}
  </form>;
}

export function submissionLabel(state: NonNullable<LocalPersonalFilingResponse['submission']>['state']): string {
  return ({ QUEUED: 'Queued', RECEIVED: 'Received', PROCESSING: 'Processing', ACKNOWLEDGED: 'Acknowledged',
    ACCEPTED: 'Accepted', REJECTED: 'Rejected', RESPONSE_UNKNOWN: 'Awaiting confirmation' })[state];
}

/** Presentation follows the actual state; provider/environment provenance stays
 * in the durable submission record rather than in ordinary customer labels. */
export function submissionMessage(state: NonNullable<LocalPersonalFilingResponse['submission']>['state'], message?: string): string {
  // Preserve genuine actionable response detail. Only replace known environment
  // scaffolding; do not alter persisted receipts, events, hashes or signatures.
  if (message?.trim() && !/simulat(?:ed|ion|or)|local (?:development|preview|test)|test submission/i.test(message)) return message;
  return ({ QUEUED: 'Your signed return is queued for submission.', RECEIVED: 'Your return has been received.',
    PROCESSING: 'Your return is being processed.', ACKNOWLEDGED: 'Receipt has been acknowledged. A final outcome is pending.',
    ACCEPTED: 'Your return has been accepted.', REJECTED: 'Your return was not accepted. Review the response before preparing a correction.',
    RESPONSE_UNKNOWN: 'Confirmation is pending. Check the existing submission before trying again.' })[state];
}

export function submissionMatchesPreparation(filing: LocalPersonalFilingResponse): boolean {
  return !filing.submission || Boolean(filing.preparation
    && filing.submission.preparationId === filing.preparation.id
    && filing.submission.artifactSha256 === filing.preparation.artifactSha256
    && filing.submission.authorityCode === filing.preparation.artifact.authorityCode);
}

export function safeAuthorityUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname && !url.username && !url.password ? url.href : undefined;
  } catch { return undefined; }
}

/** Print only the persisted version being reviewed. Text nodes prevent names/trace text becoming markup. */
export function printPersonalFilingPreview(preparation: LocalPersonalPreparation): void {
  const preview = window.open('', '_blank');
  if (!preview) throw new Error('Print window unavailable');
  preview.opener = null;
  const document = preview.document;
  document.title = `Amoye return preview ${preparation.artifact.taxYear}`;
  const style = document.createElement('style');
  style.textContent = 'body{font:14px/1.5 system-ui,sans-serif;color:#14231e;margin:40px;max-width:780px}h1{font-size:24px}table{border-collapse:collapse;width:100%}td,th{padding:8px;text-align:left;border-bottom:1px solid #ccc}small{display:block;margin-top:18px}button{padding:10px 18px}@media print{button{display:none}body{margin:0}@page{margin:18mm}}';
  document.head.append(style);
  const append = (tag: string, text: string) => { const element = document.createElement(tag); element.textContent = text;
    document.body.append(element); return element; };
  const { artifact } = preparation;
  append('h1', `Amoye · ${artifact.returnType}`);
  append('p', 'Amoye return summary — Not a prescribed filing form or authority receipt.');
  append('p', `${artifact.taxpayerName} · ${artifact.taxYear} · ${artifact.authorityLabel}`);
  append('p', `Status: ${preparation.status === 'SIGNED' ? 'Signed' : 'Prepared, unsigned'}`);
  append('p', `Income: ${formatKobo(artifact.facts.grossIncomeKobo)} · Tax credits: ${formatKobo(artifact.facts.taxCreditsKobo)} · Estimated balance: ${formatKobo(artifact.liabilityKobo)}`);
  append('h2', 'How your tax was calculated');
  const table = document.createElement('table');
  for (const line of artifact.calculation.trace) {
    const row = table.insertRow(); row.insertCell().textContent = line.rule;
    row.insertCell().textContent = formatKobo(line.resultKobo);
  }
  document.body.append(table);
  if (artifact.residencyReview) {
    const review = artifact.residencyReview;
    append('h2', 'Basis for the filing authority');
    append('p', 'User-declared facts and explanation; residence is not independently verified.');
    append('p', review.basis || 'No explanation recorded.');
    append('p', review.discrepancyNotes); append('p', review.ruleBasis);
    append('p', review.reviewState === 'EXPLANATION_RECORDED' ? 'Explanation recorded.' : 'Follow-up needed.');
    for (const prompt of review.prompts) append('p', prompt);
    for (const evidence of review.evidence) append('p', evidence.filename);
  }
  append('h2', 'Declaration'); append('p', artifact.declaration.text);
  append('p', preparation.signature ? `Confirmation recorded: ${new Date(preparation.signature.signedAt).toLocaleString()}` : 'Not signed.');
  append('small', `Saved return version ${preparation.version}. This preview is not proof of filing or payment.`);
  const printButton = append('button', 'Print / Save PDF');
  printButton.addEventListener('click', () => preview.print());
  preview.focus(); preview.print();
}
